/**
 * Routing berbasis HOST untuk apex platform `kelolify.com`.
 *
 * MURNI — tanpa React/Supabase/I-O. Seluruh keputusan "request ini milik
 * halaman platform atau milik toko" tinggal di sini supaya `src/middleware.ts`
 * hanya memanggil satu fungsi murah di baris pertamanya. Diuji di
 * `__tests__/platformHost.test.ts`.
 *
 * BATAS DENGAN FASE 6: hari ini `isPlatformHost() === false` berarti "toko
 * Galea". Nanti, saat resolver toko multi-tenant mendarat, `false` berubah
 * makna jadi "cari tokonya di database" — satu cabang `else` di pemanggil,
 * bukan tulis ulang file ini.
 */

/**
 * Apex platform. HANYA host di daftar ini — subdomain toko BUKAN platform.
 *
 * SELURUH domain LAMA tetap dikenali (`mitrahub.id` pindah ke `kelolify.com`
 * 2026-09; `mitrahub.space` pindah ke `mitrahub.id` 2026-08). Bila DNS lamanya
 * masih/kembali menunjuk deployment ini, request-nya harus tetap terbaca
 * "platform": jatuh ke jalur toko berarti RPC menjawab `unclaimed` dan perute
 * melemparnya ke apex — persis lingkaran yang terukur di situs live
 * 2026-09-08, saat `kelolify.com` (belum ada di daftar ini) di-307 ke
 * `mitrahub.id` yang saat itu menjawab 503. Redirect lama→baru ada di
 * `next.config.mjs`; daftar ini lapis keduanya.
 *
 * ⛔ Daftar ini KEMBAR dengan langkah 2 RPC `resolve_store_public` (mig 5590).
 * Menambah host di satu sisi saja = host itu platform bagi middleware tapi
 * `unclaimed` bagi database (atau sebaliknya). Ubah keduanya, sekali jalan.
 */
const PLATFORM_HOSTS: readonly string[] = [
  "kelolify.com",
  "www.kelolify.com",
  "mitrahub.id",
  "www.mitrahub.id",
  "mitrahub.space",
  "www.mitrahub.space",
];

/** Dipakai bila `NEXT_PUBLIC_PLATFORM_HOST` kosong/salah bentuk. */
const DEFAULT_PLATFORM_BASE = "https://kelolify.com";

/** Dipakai bila `NEXT_PUBLIC_SITE_URL` kosong/salah bentuk. */
const DEFAULT_STORE_BASE = "https://galeastore.id";

/** Pola hostname sah (cermin `HOSTNAME_RE` di `src/lib/seo.ts`). */
const HOSTNAME_RE =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;

/**
 * Normalisasi SATU nilai host jadi hostname telanjang.
 *
 * Daftar dipisah koma tetap diterima demi ketahanan (nilai header yang cacat
 * tak boleh membuat resolusi meledak) dan entri PERTAMA yang diambil — tapi
 * JANGAN memakai sifat itu untuk memilih host sebuah request: pemilihannya ada
 * di `requestHostname()` di bawah, yang tahu header mana yang boleh dipercaya.
 *
 * ⚠️ DIEKSPOR SENGAJA: `src/lib/storeHost.ts` WAJIB memakai normalizer yang
 * sama persis. Dua normalizer yang melenceng = lubang keamanan, bukan sekadar
 * duplikasi: satu host bisa lolos cek platform di sini lalu dibaca sebagai
 * subdomain toko di sana (atau sebaliknya). Kalau file ini butuh normalizer
 * berbeda suatu saat, ubah SATU tempat ini dan perbaiki pemanggilnya.
 */
export function normalizeHost(raw: string | null | undefined): string {
  if (!raw) return "";
  return (
    raw
      .split(",")[0]
      .trim()
      .toLowerCase()
      // buang port: "mitrahub.space:3000" → "mitrahub.space"
      .replace(/:\d+$/, "")
      // buang titik FQDN di ekor: "mitrahub.space." → "mitrahub.space"
      .replace(/\.+$/, "")
  );
}

/**
 * True bila string sudah berbentuk hostname sah (label alfanumerik dipisah
 * titik, ditutup TLD huruf). Tidak menormalkan apa pun — beri ia keluaran
 * `normalizeHost()`.
 *
 * ⚠️ DIEKSPOR SENGAJA, alasan sama dengan `normalizeHost`: `storeHost.ts`
 * harus memakai pola yang sama. Menyalin regex-nya ke file ketiga berarti
 * cepat atau lambat dua file akan berbeda pendapat soal host yang sama.
 */
export function isHostnameLike(host: string): boolean {
  return HOSTNAME_RE.test(host);
}

/** Daftar host platform — dibaca `storeHost.ts` untuk invarian ujinya. */
export function platformHosts(): readonly string[] {
  return PLATFORM_HOSTS;
}

/** Cukup `Headers` maupun objek apa pun yang punya `get` — supaya helper di
 *  bawah bisa dipanggil dari middleware, route handler, dan unit test. */
type HeaderReader = { get(name: string): string | null };

/**
 * HOST YANG BERLAKU untuk sebuah request — SATU-SATUNYA jawaban atas
 * "request ini milik toko siapa". Dipakai middleware DAN seluruh route server
 * (lewat `requestHost()` di `storeContextServer.ts`, yang mendelegasikan ke sini).
 *
 * ⛔ `Host` LEBIH DULU, `x-forwarded-host` HANYA cadangan. Urutannya dulu
 * terbalik, dan itu lubang keamanan, bukan sekadar selera: di Hostinger header
 * `x-forwarded-host` TIDAK disetel maupun ditimpa edge (hcdn), jadi nilai yang
 * dikirim klien sampai utuh ke aplikasi dan MENANG. Akibatnya penelepon mana
 * pun bisa memilih toko mana yang dilayani — dan setiap gerbang host di repo
 * ini (404 `/platform*`, `notFound()` panel platform, gerbang T4 lintas-domain,
 * stamp `store_id` pendaftaran, scope Setup Admin, pemilihan token CAPI)
 * membaca nilai yang sama. Diukur di situs live 2026-09-03
 * (bughunt `docs/bughunt/G2-routing-domain.md` §G2-1):
 *
 *     curl -sI -H 'X-Forwarded-Host: mitrahub.id' https://galeastore.id/platform/login
 *       → 200 (layar login panel platform, di domain toko; tanpa header: 404)
 *
 * KENAPA `Host` AMAN DIPERCAYA di sini, dan bukan tebakan: matcher
 * `has: { type: "host" }` di `next.config.mjs` membaca header `Host` mentah,
 * dan pada request nyata ia tetap melihat host asli walau `x-forwarded-host`
 * dipalsukan ke nilai lain — di kedua arah (kontrol positif & negatif di
 * laporan yang sama). Jadi `Host` memang tiba utuh di origin.
 *
 * Cadangannya mengambil entri TERAKHIR, bukan pertama: dalam rantai
 * `X-Forwarded-*` entri pertama adalah ujung yang ditulis KLIEN, dan yang
 * ditambahkan proxy terdekat ada di belakang. Cadangan ini hanya terpakai bila
 * `Host` benar-benar tak ada — pelanggaran HTTP/1.1 — sehingga bukan jalur yang
 * bisa dipilih penyerang selama Host terkirim.
 */
export function requestHostname(headers: HeaderReader): string {
  const direct = normalizeHost(headers.get("host"));
  if (direct) return direct;

  const forwarded = headers.get("x-forwarded-host") ?? "";
  const parts = forwarded.split(",");
  return normalizeHost(parts[parts.length - 1] ?? "");
}

/**
 * True HANYA untuk apex platform (`kelolify.com`, `www.kelolify.com`, + domain
 * lama `mitrahub.id` & `mitrahub.space`).
 *
 * ⛔ SENGAJA pencocokan PERSIS, bukan `endsWith(".kelolify.com")`. Sufiks
 * akan menelan dua hal sekaligus: (1) `<slug>.kelolify.com` — persis ruang
 * alamat toko penyewa sejak mig 5770 memindahkan basis subdomain ke sini,
 * sehingga setiap toko akan tampil sebagai halaman jualan platform; dan
 * (2) host jahat semacam `jahat-kelolify.com` yang bukan milik kita.
 */
export function isPlatformHost(rawHost: string | null | undefined): boolean {
  const host = normalizeHost(rawHost);
  return host !== "" && PLATFORM_HOSTS.includes(host);
}

/** Buang spasi & trailing slash supaya aman digabung dengan path. */
function trimBase(raw: string): string {
  return raw.trim().replace(/\/+$/, "");
}

/**
 * Ubah nilai env jadi base URL absolut. Menerima `example.com` maupun
 * `https://example.com/`. Nilai yang bukan hostname sah → `null` (pemanggil
 * jatuh ke default) — fail-safe: salah isi env tak boleh merusak redirect.
 */
function baseFromEnv(raw: string | undefined): string | null {
  const value = trimBase(raw ?? "");
  if (!value) return null;
  const host = value
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/:\d+$/, "")
    .replace(/\.+$/, "")
    .toLowerCase();
  if (!host || !HOSTNAME_RE.test(host)) return null;
  return `https://${host}`;
}

/**
 * Basis URL absolut halaman platform (default `https://kelolify.com`).
 *
 * KONSTANTA DULU, env belakangan: `NEXT_PUBLIC_PLATFORM_HOST` hanya dipakai
 * kalau memang terisi, sehingga pekerjaan ini TIDAK menuntut env baru di
 * hPanel (memasang env `NEXT_PUBLIC_*` memicu rebuild otomatis, dan rebuild
 * itu men-deploy `main` terbaru — efek samping yang tak diminta).
 */
export function platformBaseUrl(): string {
  return baseFromEnv(process.env.NEXT_PUBLIC_PLATFORM_HOST) ?? DEFAULT_PLATFORM_BASE;
}

/**
 * Alamat halaman jualan platform (`https://kelolify.com/`) untuk DITAUTKAN
 * dari halaman toko — Login, Daftar, Akun Saya (dulu halaman lokal
 * `/developer`, kini dihapus).
 *
 * Ada sebagai fungsi tersendiri supaya tautan itu punya SATU definisi: kalau
 * tiap layar merangkai `${platformBaseUrl()}/` sendiri-sendiri, cepat atau
 * lambat ada yang lupa garis miringnya atau menulis host-nya sebagai literal.
 *
 * ⛔ Harus ABSOLUT, bukan path `/platform`: di domain toko, path itu bukan
 * milik platform (dan `/` adalah beranda toko). Aman dipanggil dari komponen
 * klien — `NEXT_PUBLIC_PLATFORM_HOST` di-inline saat build.
 */
export function platformLandingUrl(): string {
  return `${platformBaseUrl()}/`;
}

/**
 * Target redirect untuk path TOKO yang masih diakses lewat apex platform.
 * Path + query dipertahankan apa adanya supaya tautan lama (mis. share produk
 * ke WhatsApp ber-`?ref=`) mendarat di halaman yang sama, hanya pindah domain.
 */
export function storeRedirectUrl(pathnameWithSearch: string): string {
  const base =
    baseFromEnv(process.env.NEXT_PUBLIC_SITE_URL) ??
    baseFromEnv(process.env.SITE_URL) ??
    DEFAULT_STORE_BASE;
  const p = (pathnameWithSearch ?? "").trim();
  if (!p || p === "/") return `${base}/`;
  return `${base}${p.startsWith("/") ? "" : "/"}${p}`;
}
